import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import CandidateCard from "./components/cards/CandidateCard";

export default function ForBusinesses() {
  // Keep the original images for other sections
  const images = [
    "/asset/ForBusiness/Job seeker yearly1.png",
    "/asset/ForBusiness/Job seeker yearly2.png",
    "/asset/ForBusiness/Job seeker yearly3.png"
  ];
  
  // Candidate data - same as used in Homepage
  const candidates = [
    {
      name: "Derek Anderson",
      experience: "5 Years of sales experience",
      recommendations: "3 recommendations",
      image: "/asset/HomePage/1.png"
    },
    {
      name: "Farah Haddad",
      experience: "5 Years of hospitality experience",
      recommendations: "3 recommendations",
      image: "/asset/HomePage/2.png"
    },
    {
      name: "Mia Sheikh",
      experience: "2 Years of retail experience",
      recommendations: "2 recommendations",
      image: "/asset/HomePage/3.png"
    },
    {
      name: "Michael Miller",
      experience: "12 Years of management experience",
      recommendations: "7 recommendations",
      image: "/asset/HomePage/4.png"
    },
    {
      name: "Laura Chen",
      experience: "3 Years of hospitality experience",
      recommendations: "4 recommendations",
      image: "/asset/HomePage/5.png"
    },
    {
      name: "James Wilson",
      experience: "4 Years of service experience",
      recommendations: "5 recommendations",
      image: "/asset/HomePage/6.png"
    }
  ];

  // State for both carousels
  const [imageIndex, setImageIndex] = useState(1);
  const [candidateIndex, setCandidateIndex] = useState(1);
  const [isDragging, setIsDragging] = useState(false);
  const [isHovered, setIsHovered] = useState(false);

  // Timer for image carousel
  useEffect(() => {
    const imageTimer = setInterval(() => {
      if (!isDragging && !isHovered) {
        setImageIndex((prev) => (prev === images.length - 1 ? 0 : prev + 1));
      }
    }, 3000);
    return () => clearInterval(imageTimer);
  }, [isDragging, isHovered, images.length]);
  
  // Timer for candidate carousel
  useEffect(() => {
    const candidateTimer = setInterval(() => {
      if (!isDragging && !isHovered) {
        setCandidateIndex((prev) => (prev === candidates.length - 1 ? 0 : prev + 1));
      }
    }, 3000);
    return () => clearInterval(candidateTimer);
  }, [isDragging, isHovered, candidates.length]);

  // Handler for image carousel
  const handleImageDragEnd = (event, info) => {
    const swipeThreshold = 50;
    if (Math.abs(info.offset.x) > swipeThreshold) {
      if (info.offset.x > 0) {
        setImageIndex((prev) => (prev === 0 ? images.length - 1 : prev - 1));
      } else {
        setImageIndex((prev) => (prev === images.length - 1 ? 0 : prev + 1));
      }
    }
    setIsDragging(false);
  };
  
  // Handler for candidate carousel
  const handleCandidateDragEnd = (event, info) => {
    const swipeThreshold = 50;
    if (Math.abs(info.offset.x) > swipeThreshold) {
      if (info.offset.x > 0) {
        setCandidateIndex((prev) => (prev === 0 ? candidates.length - 1 : prev - 1));
      } else {
        setCandidateIndex((prev) => (prev === candidates.length - 1 ? 0 : prev + 1));
      }
    }
    setIsDragging(false);
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section with Candidate Cards */}
      <div className="relative bg-[#FFF5EB] pt-16 pb-32 z-0">
        <div className="max-w-7xl mx-auto px-4 relative z-10">
          {/* Section Title */}
          <div className="text-center mb-8">
            <h1 className="text-4xl font-bold mb-4">
              Meet Your Next <span className="text-[#F47521]">Star Candidates</span>
            </h1>
            <p className="text-gray-600 text-lg max-w-3xl mx-auto">
              Hire top talent quickly and efficiently with Hiree's verified candidates
            </p>
          </div>

          {/* Candidate Card Carousel */}
          <div 
            className="flex justify-center items-center h-[450px] relative overflow-visible"
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
          >
            <div className="absolute w-full flex justify-center items-center z-20">
              <AnimatePresence initial={false}>
                {candidates.map((candidate, index) => {
                  const position = (index - candidateIndex + candidates.length) % candidates.length;
                  const isCenter = position === 0;
                  const isLeft = position === candidates.length - 1;
                  const isRight = position === 1;
                  
                  if (!isLeft && !isCenter && !isRight) return null;

                  return (
                    <motion.div
                      key={`candidate-${index}`}
                      className="absolute"
                      initial={{ 
                        x: isRight ? 300 : isLeft ? -300 : 0,
                        scale: 0.8,
                        opacity: 0,
                        rotateY: isRight ? 15 : isLeft ? -15 : 0
                      }}
                      animate={{ 
                        x: isRight ? 300 : isLeft ? -300 : 0,
                        scale: isCenter ? 1 : 0.85,
                        opacity: isCenter ? 1 : 0.7,
                        rotateY: isRight ? 15 : isLeft ? -15 : 0,
                        zIndex: isCenter ? 20 : 10
                      }}
                      exit={{ 
                        x: isRight ? 600 : isLeft ? -600 : 0,
                        scale: 0.8,
                        opacity: 0
                      }}
                      transition={{
                        type: "spring",
                        stiffness: 300,
                        damping: 30
                      }}
                      drag="x"
                      dragConstraints={{ left: 0, right: 0 }}
                      onDragStart={() => setIsDragging(true)}
                      onDragEnd={handleCandidateDragEnd}
                      style={{
                        cursor: isDragging ? "grabbing" : "grab",
                        filter: !isCenter ? 'brightness(0.9)' : 'brightness(1)'
                      }}
                    >
                      <CandidateCard
                        image={candidate.image}
                        name={candidate.name}
                        experience={candidate.experience}
                        recommendations={candidate.recommendations}
                        candidateId={`candidate-${index}`}
                        isStarCandidate={isCenter} // Only center candidate is a star
                      />
                    </motion.div>
                  );
                })}
              </AnimatePresence>
            </div>
            
            {/* Shuffle Controls */}
            <div className="absolute bottom-[-60px] flex gap-4 justify-center w-full">
              <div className="flex items-center gap-2 bg-white px-4 py-2 rounded-full shadow-lg z-30 relative">
                <button 
                  onClick={() => {
                    setCandidateIndex(prevIndex => 
                      prevIndex === 0 ? candidates.length - 1 : prevIndex - 1
                    );
                  }}
                  className="w-8 h-8 rounded-full bg-[#F47521] text-white flex items-center justify-center"
                >
                  ←
                </button>
                <span className="text-sm px-2">Shuffle Cards</span>
                <button 
                  onClick={() => {
                    setCandidateIndex(prevIndex => 
                      prevIndex === candidates.length - 1 ? 0 : prevIndex + 1
                    );
                  }}
                  className="w-8 h-8 rounded-full bg-[#F47521] text-white flex items-center justify-center"
                >
                  →
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 py-12">
        {/* Headline */}
        <div className="text-center mb-12">
          <h1 className="text-5xl font-bold mb-4">
            Hire Smarter, Faster, and With Confidence!
          </h1>
          <div className="relative mb-6">
            <img 
              src="/asset/ForBusiness/yellowLines.png" 
              alt="Decorative line"
              className="w-[400px] h-7 mx-auto -mt-3"
            />
          </div>
          <p className="text-gray-600 max-w-2xl mx-auto text-lg mb-6">
            Hiree simplifies the process, helping businesses connect with trusted job 
            seekers instantly. With verified references, AI-powered job matching, and instant 
            applications, we ensure that employers find the right candidates faster.
          </p>
          <button className="bg-[#F47521] text-white px-10 py-4 rounded-full font-semibold text-lg hover:bg-orange-600 transition-colors">
            Start Hiring
          </button>
        </div>

        {/* AI Feature Section */}
        <div className="bg-white rounded-[32px] shadow-lg p-12 mb-16 relative overflow-hidden">
          {/* Frame */}
          <div className="absolute inset-0 rounded-[32px] pointer-events-none" 
            style={{
              background: 'linear-gradient(45deg, #FFF5EB, #FFE4BC, #FFDBA5, #FFD192)',
              padding: '2px'
            }}>
            <div className="w-full h-full bg-white rounded-[32px]"></div>
          </div>
          
          <div className="relative">
            {/* headline */}
            <div className="flex justify-center items-center gap-2 mb-8">
              <span className="text-[#EC4899] text-4xl leading-none">★</span>
              <h2 className="text-3xl font-bold">AI pre screening and candidate matching</h2>
            </div>

            <div className="flex gap-12">
              {/* Left side content */}
              <div className="w-[45%]">
                <div className="flex gap-2">
                  <img 
                    src="/asset/ForBusiness/ranking.png"
                    alt="Candidate Ranking"
                    className="w-[220px] h-auto relative -translate-y-6"
                  />
                  <img 
                    src="/asset/ForBusiness/ai assistant.png"
                    alt="AI Assistant Chat"
                    className="w-[220px] h-auto relative translate-y-6"
                  />
                </div>
              </div>

              {/* Feature List */}
              <div className="w-[55%] space-y-4">
                <p className="text-gray-700 text-xl font-semibold mb-6">
                  AI Assistant streamlines the hiring process, helping 
                  employers identify top candidates faster and more 
                  efficiently.
                </p>
                <div className="flex items-start gap-2">
                  <span className="text-green-500 text-xl">✓</span>
                  <div>
                    <strong className="text-[#2B3674] mr-1">Automated Interview Questions</strong>
                    <span className="text-gray-600">– Our AI can conduct pre-screening interviews, asking industry-specific questions based on the role.</span>
                  </div>
                </div>
                <div className="flex items-start gap-2">
                  <span className="text-green-500 text-xl">✓</span>
                  <div>
                    <strong className="text-[#2B3674] mr-1">Instant Candidate Insights</strong>
                    <span className="text-gray-600">– Get a summary of key responses, highlighting strengths and potential concerns.</span>
                  </div>
                </div>
                <div className="flex items-start gap-2">
                  <span className="text-green-500 text-xl">✓</span>
                  <div>
                    <strong className="text-[#2B3674] mr-1">Consistency in Evaluation</strong>
                    <span className="text-gray-600">– Remove bias and ensure every candidate is assessed using the same criteria.</span>
                  </div>
                </div>
                <div className="flex items-start gap-2">
                  <span className="text-green-500 text-xl">✓</span>
                  <div>
                    <strong className="text-[#2B3674] mr-1">Saves Time</strong>
                    <span className="text-gray-600">– Reduce time spent on initial interviews by reviewing AI-generated candidate responses.</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* AI Hiring Button */}
        <div className="text-center -mt-4 mb-16">
          <button className="bg-[#EC4899] text-white px-10 py-3 rounded-full font-semibold text-lg hover:bg-pink-600 transition-colors">
            Try AI Hiring Now
          </button>
        </div>

        {/* Pricing Comparison */}
        <div className="relative max-w-5xl mx-auto">
          <div className="flex justify-center items-center gap-0">
            <div className="bg-white rounded-xl shadow-lg p-2 w-60 border-2 border-gray-200">
              <h3 className="text-gray-400 text-base font-bold mb-1">Job Boards</h3>
              <p className="text-gray-400 text-lg font-bold mb-2">$500 - $1000/hire</p>
              <div className="mt-2">
                <p className="text-gray-600 text-sm mb-1">Extra fee per candidate search</p>
                <p className="text-gray-600 text-sm">Extra fee per applicants</p>
              </div>
            </div>

            <div className="relative bg-[#FFF5EB] shadow-xl w-72 z-10 border-2 border-[#F47521] scale-105 rounded-2xl overflow-hidden">
              <div className="bg-[#F47521] text-white py-2 px-6 flex items-center justify-center gap-2">
                <span className="text-yellow-200 text-lg">★</span>
                <span className="text-lg font-semibold">Hiree</span>
                <span className="text-yellow-200 text-lg">★</span>
              </div>
              <div className="p-4">
                <h3 className="text-xl font-bold mb-2">$0 per post</h3>
                <div className="flex items-center gap-1 text-green-500 mb-3">
                  <span className="text-xs">✓</span>
                  <span className="text-sm">AI Interview Assistant</span>
                </div>
                <ul className="space-y-2 mb-4">
                  <li className="flex items-center gap-2">
                    <span className="text-green-500 text-xs">✓</span>
                    <span className="text-sm">Unlimited job posting</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <span className="text-green-500 text-xs">✓</span>
                    <span className="text-sm">Fast employer-verified feedback</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <span className="text-green-500 text-xs">✓</span>
                    <span className="text-sm">Free Applicant search</span>
                  </li>
                </ul>
                <button className="w-full bg-[#F47521] text-white py-2 text-sm">
                  Post jobs for free with AI
                </button>
              </div>
            </div>

            <div className="bg-white rounded-xl shadow-lg p-2 w-60 border-2 border-gray-200">
              <h3 className="text-gray-400 text-base font-bold mb-1">Other Platforms</h3>
              <p className="text-gray-400 text-lg font-bold mb-2">$300-500/month</p>
              <div className="mt-2">
                <p className="text-gray-600 text-sm mb-1">Limited job posting</p>
                <p className="text-gray-600 text-sm">Unverified employees</p>
              </div>
            </div>
          </div>
        </div>

        <div className="h-16"></div>

        {/* Mobile App Feature */}
        <div className="flex items-center justify-between mb-16 max-w-5xl mx-auto">
          <div className="w-1/2 flex justify-start relative">
            <div className="relative w-full rounded-3xl bg-[#FFF5EB] flex items-center justify-center p-12">
              <div className="relative">
                <img 
                  src="/asset/ForBusiness/iMockup - iPhone 15 Pro Max.png" 
                  alt="Mobile App Preview 1"
                  className="w-[240px] h-auto relative z-10 -translate-x-16 -translate-y-20"
                />
                <img 
                  src="/asset/ForBusiness/iMockup2 - iPhone 15 Pro Max.png" 
                  alt="Mobile App Preview 2"
                  className="w-[240px] h-auto absolute top-0 left-0 z-20 translate-x-20 translate-y-18"
                />
              </div>
            </div>
          </div>

          <div className="w-1/2 pl-12">
            <h2 className="text-4xl font-bold mb-4">
              Hire faster in your area<br />
              anytime, any where
            </h2>
            <p className="text-gray-600 text-lg mb-6">
              Instantly connect with verified candidates in<br />
              your area, anytime you need them
            </p>
            <ul className="space-y-3 mb-8">
              <li className="flex items-start">
                <div className="w-1.5 h-1.5 rounded-full bg-[#F47521] mr-1.5 mt-[0.6rem] flex-shrink-0"></div>
                <div className="flex-1">
                  <span className="font-semibold">Instant Hiring</span>
                  <span className="text-gray-600">– Find and connect with verified candidates within minutes.</span>
                </div>
              </li>
              <li className="flex items-start">
                <div className="w-1.5 h-1.5 rounded-full bg-[#F47521] mr-0.2 mt-[0.6rem] flex-shrink-0"></div>
                <div className="flex-1">
                  <span className="font-semibold">Local Talent</span>
                  <span className="text-gray-600">– Easily hire workers near you for fast and reliable staffing.</span>
                </div>
              </li>
              <li className="flex items-center">
                <div className="w-1.5 h-1.5 rounded-full bg-[#F47521] mr-2 flex-shrink-0"></div>
                <span className="font-semibold">24/7 Access</span>
                <span className="text-gray-600">– Post jobs and hire anytime, from any device.</span>
              </li>
              <li className="flex items-center">
                <div className="w-1.5 h-1.5 rounded-full bg-[#F47521] mr-2 flex-shrink-0"></div>
                <span className="font-semibold">Seamless Experience</span>
                <span className="text-gray-600">– Post, review, and hire in just a few clicks.</span>
              </li>
            </ul>
            <button className="bg-[#F47521] text-white px-10 py-3 rounded-full text-lg font-semibold hover:bg-orange-600 transition-colors">
              Post Your Job Now
            </button>
          </div>
        </div>

        {/* Find Star Candidate Section */}
        <div className="bg-[#FFF5EB] rounded-3xl p-8 relative overflow-hidden">
          {/* Hiree Label */}
          <div className="absolute top-0 left-0 bg-[#F47521] text-white py-2 px-6 rounded-br-xl flex items-center gap-2">
            <span className="text-yellow-200 text-lg">★</span>
            <span className="text-lg font-semibold">Hiree</span>
            <span className="text-yellow-200 text-lg">★</span>
          </div>

          <div className="flex items-center justify-between pt-12">
            <div className="w-1/2">
              <h2 className="text-4xl font-bold mb-3">
                Find your next<br />
                <span className="text-[#F47521]">Star Candidate</span>
              </h2>
              <p className="text-gray-600 mb-6">
                Wherever you are, whenever you need!<br />
                Hiree makes hiring easy!
              </p>
              <button className="bg-white text-[#F47521] border-2 border-[#F47521] px-8 py-3 rounded-full hover:bg-[#F47521] hover:text-white transition-colors">
                Start your smart hire now
              </button>
            </div>
            <div className="w-1/2 flex justify-end">
              <img 
                src="/asset/ForBusiness/Group 15392.png" 
                alt="Candidate Preview"
                className="w-[90%]"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
